import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Logo from './Logo';
import { 
  Home, Brain, Wallet, MessageCircle, 
  User, LogOut, Menu, X, BarChart3, BookOpen,
  Calculator, Target, Activity, Inbox, Building2, CalendarCheck
} from 'lucide-react';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const navItems = [
  { path: '/dashboard', label: 'Home', icon: Home },
  { path: '/courses', label: 'Courses', icon: BookOpen },
  { path: '/quiz', label: 'Quiz', icon: Brain },
  { path: '/daily-quiz', label: 'Daily Quiz', icon: CalendarCheck },
  { path: '/budget-tracker', label: 'Cashflow', icon: Wallet },
  { path: '/investment-calculator', label: 'Modelling', icon: BarChart3 },
  { path: '/tools', label: 'Tools', icon: Calculator },
  { path: '/goals', label: 'Goals', icon: Target },
  { path: '/mentor', label: 'Assistant', icon: MessageCircle },
  { path: '/about', label: 'About Us', icon: Building2 },
  { path: '/health', label: 'Health Check', icon: Activity },
  { path: '/profile', label: 'Profile', icon: User },
];

// Financial Fitness Score derived from accumulated learning progress (0-100)
export const getFitnessScore = (user) => Math.min(100, Math.round((user?.xp || 0) / 20));

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const fitnessScore = getFitnessScore(user);
  const items = user?.is_admin
    ? [...navItems, { path: '/admin/leads', label: 'Leads Inbox', icon: Inbox }]
    : navItems;

  return (
    <div className="min-h-screen bg-[#0A1F44] relative">
      {/* Ambient background decoration */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-70" />
        <div className="absolute -top-40 right-[-100px] w-[560px] h-[560px] rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(192,151,92,0.12), transparent 70%)' }} />
        <div className="absolute bottom-[-140px] left-[22%] w-[480px] h-[480px] rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(29,61,114,0.5), transparent 70%)' }} />
        <div className="absolute top-1/3 left-[-120px] w-[360px] h-[360px] rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(192,151,92,0.06), transparent 70%)' }} />
      </div>
      {/* Desktop Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-[#061530] border-r border-[#1D3D72] hidden lg:flex flex-col z-50">
        {/* Logo */}
        <Link to="/dashboard" className="p-6 border-b border-[#1D3D72] block">
          <Logo variant="light" size="md" />
        </Link>

        {/* User Stats */}
        {user && (
          <div className="p-6 border-b border-[#1D3D72]">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-[#0A1F44] font-bold text-lg shadow-lg shadow-[#C0975C]/20" style={{ backgroundColor: '#C0975C' }}>
                {user.username?.[0]?.toUpperCase()}
              </div>
              <div>
                <p className="font-semibold text-white">{user.username}</p>
                <p className="text-xs text-[#94A9C9]">Precision Member</p>
              </div>
            </div>
            <div>
              <div className="flex justify-between items-end mb-1.5">
                <span className="text-xs uppercase tracking-wider text-[#94A9C9]">Financial Fitness</span>
                <span className="font-mono text-lg font-bold text-[#C0975C]">{fitnessScore}<span className="text-xs text-[#94A9C9]">/100</span></span>
              </div>
              <div className="h-2 w-full bg-[#0A1F44] overflow-hidden rounded-full">
                <div className="h-full rounded-full transition-all" style={{ width: `${fitnessScore}%`, background: 'linear-gradient(90deg,#C0975C,#D4B37F)' }} />
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-1.5">
            {items.map((item) => {
              const isActive = location.pathname === item.path || 
                             (item.path === '/quiz' && location.pathname.startsWith('/quiz'));
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                      isActive 
                        ? 'bg-[#C0975C] text-[#0A1F44] font-bold shadow-lg shadow-[#C0975C]/25' 
                        : 'text-[#94A9C9] hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="text-[13px] uppercase tracking-wider">{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-[#1D3D72]">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 w-full rounded-xl text-[#94A9C9] hover:text-[#EF4444] hover:bg-white/5 transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-[13px] uppercase tracking-wider">Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 h-16 lg:hidden z-50 flex items-center justify-between px-4 glass-header">
        <Link to="/dashboard" className="flex items-center gap-2">
          <Logo variant="light" size="sm" layout="horizontal" />
        </Link>
        
        {user && (
          <div className="flex items-center gap-4">
            <span className="text-[#C0975C] font-mono text-sm">{fitnessScore}/100</span>
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 text-white"
              data-testid="mobile-menu-button"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        )}
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-[210] lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          >
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween' }}
              className="absolute right-0 top-0 bottom-0 w-72 bg-[#061530] border-l border-[#1D3D72] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-white/10 shrink-0">
                <span className="font-bold">Menu</span>
                <button onClick={() => setMobileMenuOpen(false)}>
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              {user && (
                <div className="p-4 border-b border-white/10 shrink-0">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center text-[#0A1F44] font-bold" style={{ backgroundColor: '#C0975C' }}>
                      {user.username?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold">{user.username}</p>
                      <p className="text-xs text-[#9fb0cc]">Financial Fitness {fitnessScore}/100</p>
                    </div>
                  </div>
                </div>
              )}

              <nav className="flex-1 overflow-y-auto p-4">
                <ul className="space-y-1">
                  {items.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                      <li key={item.path}>
                        <Link
                          to={item.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                            isActive 
                              ? 'bg-[#C0975C] text-[#0A1F44] font-bold' 
                              : 'text-[#94A9C9] hover:text-white hover:bg-white/5'
                          }`}
                        >
                          <item.icon className="w-5 h-5" />
                          <span className="text-sm uppercase tracking-wider">{item.label}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </nav>

              <div className="p-4 border-t border-white/10 shrink-0">
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-4 py-3 w-full text-[#FF3333]"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="text-sm uppercase tracking-wider">Logout</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0 relative z-10">
        {children}
      </main>
    </div>
  );
};
